/**
 * PATH: packages/job-hub/src/jobs/tabs/job-qa/QaAddProductControl.tsx
 * FILE_ID: JOBHUB-QA-ADD-PRODUCT-001
 * PURPOSE: One Add product control for the selected area, checklist, or step.
 * LANDMINE: Most specific selection wins. A child edit copies inherited names
 *      onto that child and leaves the parent list alone.
 */
import { useEffect, useRef, useState, type ReactNode } from 'react'
import { QaProductSearchField } from '../../qa/QaProductSearchField'
import {
  appendProductName,
  namesToEdit,
  removeProductName,
  replaceProductName,
  resolvedProductNames,
} from '../../qa/qaProductResolve'
import type { QaArea, QaAreaExecution, QaChecklistInstance, QaItemInstance } from '../../qa/qaApi'
import { normalizeList } from '../../qa/qaApiHelpers'
import type { JobQaTabController } from './useJobQaTabController'

type ProductTarget =
  | { kind: 'step'; item: QaItemInstance; inherited: string[] }
  | { kind: 'checklist'; checklist: QaChecklistInstance; inherited: string[] }
  | { kind: 'area'; area: QaArea; inherited: string[] }

function idSet(controller: JobQaTabController, key: 'areas' | 'checklists' | 'checks'): ReadonlySet<string> {
  return controller.selection.sets?.[key] ?? new Set<string>()
}

function collectTargets(controller: JobQaTabController): ProductTarget[] {
  const execution = controller.workspace.areaExecQuery?.data as QaAreaExecution | undefined
  const areaNames = execution?.product_names ?? []
  const checks = idSet(controller, 'checks')
  if (checks.size > 0 && execution) {
    const steps: ProductTarget[] = []
    for (const checklist of execution.checklists ?? []) {
      for (const item of checklist.items ?? []) {
        if (!checks.has(item.id)) continue
        steps.push({
          kind: 'step',
          item,
          inherited: resolvedProductNames({
            itemNames: [],
            itemProduct: item.product,
            checklistNames: checklist.product_names,
            areaNames,
          }),
        })
      }
    }
    if (steps.length) return steps
  }
  const checklistIds = idSet(controller, 'checklists')
  if (checklistIds.size > 0 && execution) {
    const rows = (execution.checklists ?? [])
      .filter((checklist) => checklistIds.has(checklist.id))
      .map((checklist) => ({
        kind: 'checklist' as const,
        checklist,
        inherited: checklist.product_names?.length ? [] : areaNames,
      }))
    if (rows.length) return rows
  }
  const areaIds = idSet(controller, 'areas')
  if (areaIds.size === 0) return []
  const areas = normalizeList<QaArea>(controller.workspace.areasQuery.data)
  return areas
    .filter((area) => areaIds.has(area.id))
    .map((area) => ({ kind: 'area' as const, area, inherited: [] as string[] }))
}

function shownNames(targets: ProductTarget[]): string[] {
  const first = targets[0]
  if (!first) return []
  if (first.kind === 'step') return namesToEdit(first.item.product_names, first.inherited)
  if (first.kind === 'checklist') return namesToEdit(first.checklist.product_names, first.inherited)
  return namesToEdit(first.area.product_names, first.inherited)
}

export function QaAddProductBody({ controller }: { controller: JobQaTabController }) {
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const targets = collectTargets(controller)
  const names = shownNames(targets)

  const write = async (mutate: (current: string[]) => string[]) => {
    if (targets.length === 0 || busy) return
    setBusy(true)
    setError(null)
    try {
      await Promise.all(targets.map(async (target) => {
        const current = target.kind === 'step'
          ? namesToEdit(target.item.product_names, target.inherited)
          : target.kind === 'checklist'
            ? namesToEdit(target.checklist.product_names, target.inherited)
            : namesToEdit(target.area.product_names, target.inherited)
        const next = mutate(current)
        if (next === current) return
        if (target.kind === 'step') {
          await controller.api.setItemContent(target.item.id, { product_names: next })
        } else if (target.kind === 'checklist') {
          await controller.api.setChecklistProductNames(target.checklist.id, next)
        } else {
          await controller.api.setAreaProductNames(target.area.id, next)
        }
      }))
      controller.workspace.invalidateExecution()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save the product.')
    } finally {
      setBusy(false)
    }
  }

  if (targets.length === 0) {
    return <p className="text-sm text-muted-foreground">Select an area, checklist, or step.</p>
  }

  return (
    <div className="space-y-2">
      <QaProductSearchField
        value=""
        client={controller.client}
        disabled={busy}
        ariaLabel="Add product"
        placeholder="Search or type a product"
        onCommit={(name) => { void write((current) => appendProductName(current, name)) }}
      />
      {names.length === 0 ? (
        <p className="text-xs text-muted-foreground">No products on this selection yet.</p>
      ) : (
        <ul className="space-y-1">
          {names.map((name) => (
            <li key={name} className="flex min-w-0 items-center gap-1">
              <input
                defaultValue={name}
                aria-label={`Edit ${name}`}
                disabled={busy}
                onBlur={(event) => {
                  const next = event.target.value
                  if (next.trim() === name) return
                  void write((current) => replaceProductName(current, name, next))
                }}
                className="min-w-0 flex-1 rounded-md border border-border px-2 py-1 text-sm"
              />
              <button
                type="button"
                disabled={busy}
                aria-label={`Remove ${name}`}
                onClick={() => { void write((current) => removeProductName(current, name)) }}
                className="shrink-0 px-2 text-xs text-muted-foreground"
              >
                Remove
              </button>
            </li>
          ))}
        </ul>
      )}
      {error ? <p role="alert" className="text-xs text-destructive">{error}</p> : null}
    </div>
  )
}

export function QaAddProductButton({
  controller,
  trigger,
}: {
  controller: JobQaTabController
  trigger?: (openProducts: () => void, isOpen: boolean) => ReactNode
}) {
  const [open, setOpen] = useState(false)
  const rootRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false)
    }
    const onPointer = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false)
    }
    window.addEventListener('keydown', onKey)
    window.addEventListener('mousedown', onPointer)
    return () => {
      window.removeEventListener('keydown', onKey)
      window.removeEventListener('mousedown', onPointer)
    }
  }, [open])

  return (
    <div className="relative" ref={rootRef}>
      {trigger ? trigger(() => setOpen(true), open) : (
      <button
        type="button"
        data-testid="qa-add-product"
        aria-expanded={open}
        onClick={() => setOpen((current) => !current)}
        className="inline-flex min-h-[32px] items-center gap-1.5 rounded-md border border-border px-2 py-1 text-xs font-medium"
      >
        Add Product
      </button>
      )}
      {open ? (
        <div className="absolute left-0 top-full z-40 mt-1 w-72 max-h-[45vh] overflow-y-auto rounded-md border border-border bg-card p-2 shadow-lg">
          <QaAddProductBody controller={controller} />
        </div>
      ) : null}
    </div>
  )
}
